(() => {
  'use strict';

  const root = document.documentElement;
  if (root.hasAttribute('data-crh-script-ready')) return;
  root.setAttribute('data-crh-script-ready', 'true');

  const markReady = (element) => {
    if (element.dataset.crhInitialized === 'true') return false;
    element.dataset.crhInitialized = 'true';
    return true;
  };

  const initTabs = (scope) => {
    scope.querySelectorAll('[data-crh-tabs]').forEach((tabs) => {
      if (!markReady(tabs)) return;
      const buttons = [...tabs.querySelectorAll('[role="tab"]')];
      const galleryId = tabs.dataset.crhGallery;
      const gallery = galleryId ? document.getElementById(galleryId) : null;
      const cards = gallery ? [...gallery.querySelectorAll('[data-crh-category]')] : [];
      const emptyState = gallery?.parentElement?.querySelector('[data-crh-gallery-empty]');

      const activate = (button, focus = false) => {
        const category = button.dataset.crhFilter || 'all';
        buttons.forEach((item) => {
          const active = item === button;
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
        });
        let visibleCount = 0;
        cards.forEach((card) => {
          const visible = category === 'all' || card.dataset.crhCategory === category;
          card.hidden = !visible;
          card.setAttribute('aria-hidden', String(!visible));
          if (visible) visibleCount += 1;
        });
        gallery?.setAttribute('aria-labelledby', button.id);
        if (emptyState) emptyState.hidden = visibleCount !== 0;
        if (focus) button.focus();
      };

      buttons.forEach((button, index) => {
        button.addEventListener('click', () => activate(button));
        button.addEventListener('keydown', (event) => {
          let nextIndex = null;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % buttons.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + buttons.length) % buttons.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = buttons.length - 1;
          if (nextIndex === null) return;
          event.preventDefault();
          activate(buttons[nextIndex], true);
        });
      });
      const selectedButton = buttons.find((button) => button.getAttribute('aria-selected') === 'true') || buttons[0];
      if (selectedButton) activate(selectedButton);
    });
  };

  const initFaq = (scope) => {
    scope.querySelectorAll('[data-crh-faq]').forEach((faq) => {
      if (!markReady(faq)) return;
      const allowMultiple = faq.dataset.crhAllowMultiple === 'true';
      const items = [...faq.querySelectorAll('details')];
      items.forEach((item) => {
        const summary = item.querySelector('summary');
        if (!summary) return;
        summary.setAttribute('aria-expanded', String(item.open));
        summary.addEventListener('keydown', (event) => {
          if (!['Enter', ' '].includes(event.key)) return;
          event.preventDefault();
          item.open = !item.open;
        });
        item.addEventListener('toggle', () => {
          summary.setAttribute('aria-expanded', String(item.open));
          if (!item.open || allowMultiple) return;
          items.forEach((other) => {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  };

  const initMeasurement = (scope) => {
    scope.querySelectorAll('[data-crh-measurement-tabs]').forEach((tabs) => {
      if (!markReady(tabs)) return;
      const buttons = [...tabs.querySelectorAll('[role="tab"]')];
      const section = tabs.closest('.crh-measurement');
      const items = section ? [...section.querySelectorAll('[data-crh-mount]')] : [];
      const activate = (button) => {
        const mount = button.dataset.crhMountTarget;
        buttons.forEach((item) => {
          const active = item === button;
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
        });
        items.forEach((item) => {
          item.hidden = item.dataset.crhMount !== 'both' && item.dataset.crhMount !== mount;
        });
      };
      buttons.forEach((button, index) => {
        button.addEventListener('click', () => activate(button));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'ArrowLeft') next = (index - 1 + buttons.length) % buttons.length;
          if (event.key === 'ArrowRight') next = (index + 1) % buttons.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = buttons.length - 1;
          activate(buttons[next]);
          buttons[next].focus();
        });
      });
    });
  };

  const initComparisons = (scope) => {
    scope.querySelectorAll('[data-crh-comparison]').forEach((comparison) => {
      if (!markReady(comparison)) return;
      const range = comparison.querySelector('input[type="range"]');
      if (!range) return;
      const update = () => comparison.style.setProperty('--crh-position', `${range.value}%`);
      range.addEventListener('input', update);
      update();
    });
  };

  const initDialogs = (scope) => {
    scope.querySelectorAll('[data-crh-dialog-open]').forEach((button) => {
      if (!markReady(button)) return;
      const dialog = document.getElementById(button.dataset.crhDialogOpen);
      if (!dialog || typeof dialog.showModal !== 'function') return;
      button.addEventListener('click', () => dialog.showModal());
    });
    scope.querySelectorAll('[data-crh-dialog-close]').forEach((button) => {
      if (!markReady(button)) return;
      button.addEventListener('click', () => button.closest('dialog')?.close());
    });
    scope.querySelectorAll('dialog.crh-dialog').forEach((dialog) => {
      if (dialog.dataset.crhBackdropReady === 'true') return;
      dialog.dataset.crhBackdropReady = 'true';
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    });
  };

  const initAll = (scope = document) => {
    initTabs(scope);
    initFaq(scope);
    initMeasurement(scope);
    initComparisons(scope);
    initDialogs(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
